/* eslint-disable jsx-a11y/label-has-associated-control */
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
  Stack,
  DialogActions,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Iconify from '../../../../reusable-components/iconify';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import FileThumbnail from '../../../../reusable-components/file-thumbnail';
import { uploadInvoiceFiles } from '../../../../redux/slices/invoicing';
import { useDispatch } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';

ManualInvoiceUploader.propTypes = {
  openInvoiceUploader: PropTypes.bool,
  setOpenInvoiceUploader: PropTypes.func,
};
export default function ManualInvoiceUploader({ setOpenInvoiceUploader, openInvoiceUploader }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [pdfFiles, setPdfFiles] = useState(null);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDropzone,
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // .docx
      'application/msword': [], // .doc
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // .xlsx
      'application/vnd.ms-excel': [], // .xls
    },
    onDrop: (acceptedFiles) => {
      handleFileInputChange({ target: { files: acceptedFiles } });
    },
  });

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    setOpenInvoiceUploader(false);
    setPdfFiles(null);
  };

  const handleFileInputChange = (event) => {
    const { files } = event.target;
    if (files) {
      const newFiles = Array.from(files);
      const mergedFiles = pdfFiles ? [...pdfFiles, ...newFiles] : newFiles;

      setPdfFiles(mergedFiles);
    }
  };

  const handleRemoveImage = (index) => {
    const removedPictures = [...pdfFiles];
    removedPictures.splice(index, 1);
    setPdfFiles(removedPictures);
  };

  const upload = async () => {
    const result = await dispatch(uploadInvoiceFiles(pdfFiles));
    const invoiceIds = result.data;
    localStorage.setItem('selectedInvoices', JSON.stringify(invoiceIds || []));
    navigate(`/dashboard/invoicing/new/${invoiceIds[0]}`);
    setOpenInvoiceUploader(false);
  };

  return (
    <>
      <Dialog open={openInvoiceUploader} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          <CloseIconButton onClick={handleClose}/>
          <Typography color="secondary" variant="h6" sx={{ mt: '20px' }}>
            Manual invoice
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: 'dashed 1px #4ca5e7',
              borderRadius: '4px',
              height: '250px',
              cursor: 'pointer',
              m: '10px',
            }}
            {...getRootProps()}
            onClick={() => {
              openDropzone();
            }}
          >
            <input {...getInputProps()} />
            <Stack spacing={1} direction={'column'} justifyContent={'center'} alignItems={'center'} height={'100%'}>
              <Icon icon="arcticons:folder-upload" width="5rem" height="5rem" color="#8dc63f" />
              <Stack direction={'row'}>
                <Typography color="text.secondary">Drag and drop or &nbsp;</Typography>
                <Typography color="text.secondary" sx={{ textDecoration: 'underline' }}>
                  click here
                </Typography>
                <Typography color="text.secondary">&nbsp; to upload file(s) </Typography>
              </Stack>
            </Stack>
          </Box>
          <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ mt: 1 }}>
            {pdfFiles?.map((file, index) => (
              <Grid item xs={2} key={index}>
                <Box
                  m={1}
                  boxShadow={2}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FileThumbnail key={file} file={file} tooltip sx={{ width: 32, height: 32 }} />

                  <IconButton onClick={() => handleRemoveImage(index)}>
                    <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="secondary"
            size="medium"
            disabled={!pdfFiles}
            sx={{ width: 150 }}
            onClick={upload}
          >
            Upload
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
